/** @format */

import axios, { AxiosRequestConfig } from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";
import {
    IBusiness,
    IBusinessMembership,
    IAdminBusinessListPage,
    ICollectSystemInfo,
    IQuickBooksConnection,
    IQuickBooksConnectStartResponse,
    ISyncJob,
    ICollectCustomer,
    ICollectInvoice,
    IArAgingSummary,
    IDashboardOverview,
    ICollectionsMetrics,
    CollectionsRange,
    IKpiBreakdown,
    KpiBreakdownMetric,
    IAutopilotSummary,
    IActivityFeedPage,
    IActionCenter,
    IRecoveryImpact,
    ImpactRange,
    IRelationshipSafety,
    IRecoveryFunnel,
    IRecoveryHealth,
    IRecoveryTrends,
    IDunningCampaign,
    IReminderTemplate,
    IDunningEvent,
    IInvoiceTimelineEntry,
    IRecoveryStateMetadata,
    IRecoveryStateTransition,
    ITransitionInvoiceStatePayload,
    IPromiseToPay,
    ICreatePromisePayload,
    IResolvePromisePayload,
    IListPromisesQuery,
    IRecoveryQueueQuery,
    IRecoveryQueuePage,
    IRecoveryQueueSummary,
    IAssignInvoicePayload,
    IEscalationDecision,
    ICollectPagedResponse,
    ICreateBusinessPayload,
    IUpdateBusinessSettingsPayload,
    IUpsertCampaignPayload,
    IUpsertTemplatePayload,
    ISendManualReminderPayload,
    IListInvoicesQuery,
    IListCustomersQuery,
    IListTemplatesQuery,
    IListDunningEventsQuery,
    ICustomerReply,
    IListRepliesQuery,
    IRepliesSummary,
    IResolveReplyPayload,
    IManualIngestReplyPayload,
    ISupportTicket,
    ISupportTicketDetail,
    ISupportTicketListPage,
    ICreateSupportTicketPayload,
    IAdminSupportTicketListPage,
    IAdminSupportTicketDetail,
    IAdminSupportListQuery,
    IAdminUpdateSupportTicketPayload,
    ISupportTicketMessage,
} from "../../utility/types/collectTypes";

// ─────────────────────────────────────────────────────────────────────────
// Collect / AARRP API surface.
//
// All endpoints except `submitListMyBusinesses` and `submitCreateBusiness`
// require an `x-business-id` header. Callers MUST pass the active
// businessId; the slice keeps it in state and supplies it through the
// async-thunk wrappers.
//
// Auth (Cognito Bearer token) is injected automatically by the global
// axios interceptor in `axiosConfig/axiosInterceptor.ts`.
// ─────────────────────────────────────────────────────────────────────────

function withBusiness(
    businessId: string,
    extra: AxiosRequestConfig = {},
): AxiosRequestConfig {
    return {
        ...extra,
        headers: {
            ...(extra.headers || {}),
            "x-business-id": businessId,
        },
    };
}

function pickError(error: any, fallback: string): string {
    return error?.response?.data?.message || error?.message || fallback;
}

// ── Business management ─────────────────────────────────────────────────
export const submitListMyBusinesses = async (): Promise<
    IBusinessMembership[]
> => {
    try {
        const res = await axios.get(`${API_BASE_URL}/collect/businesses`);
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load businesses."));
    }
};

export const submitCreateBusiness = async (
    payload: ICreateBusinessPayload,
): Promise<IBusiness> => {
    try {
        const res = await axios.post(`${API_BASE_URL}/collect/businesses`, payload);
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to create business."));
    }
};

export const submitGetActiveBusiness = async (
    businessId: string,
): Promise<{ business: IBusiness; role: string }> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/business`,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load business."));
    }
};

export const submitUpdateBusinessSettings = async (
    businessId: string,
    payload: IUpdateBusinessSettingsPayload,
): Promise<IBusiness> => {
    try {
        const res = await axios.put(
            `${API_BASE_URL}/collect/business/settings`,
            payload,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to update business settings."));
    }
};

// ── Super-admin console ─────────────────────────────────────────────────
// Global "am I a platform super admin?" probe. Not business-scoped — it must
// resolve regardless of which (if any) business is active, so no x-business-id.
export const submitGetAdminMe = async (): Promise<boolean> => {
    try {
        const res = await axios.get(`${API_BASE_URL}/collect/admin/me`);
        return !!res.data?.data?.isSuperAdmin;
    } catch (e: any) {
        // A non-super-admin (or unauthenticated) principal is simply "not admin".
        return false;
    }
};

export const submitGetCollectSystemInfo = async (): Promise<ICollectSystemInfo> => {
    try {
        const res = await axios.get(`${API_BASE_URL}/collect/system-info`);
        const quickBooksEnvironment = res.data?.data?.quickBooksEnvironment;
        return {
            quickBooksEnvironment:
                quickBooksEnvironment === "sandbox" ||
                    quickBooksEnvironment === "production"
                    ? quickBooksEnvironment
                    : "unknown",
        };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load system information."));
    }
};

export const submitGetAllBusinesses = async (params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: "active" | "inactive" | "all";
}): Promise<IAdminBusinessListPage> => {
    try {
        const res = await axios.get(`${API_BASE_URL}/collect/admin/businesses`, {
            params,
        });
        const { data, page, limit, total, totalPages, hasNext, hasPrev } =
            res.data;
        return { data, page, limit, total, totalPages, hasNext, hasPrev };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load businesses."));
    }
};

// ── QuickBooks connection ───────────────────────────────────────────────
export const submitStartQuickBooksConnect = async (
    businessId: string,
): Promise<IQuickBooksConnectStartResponse> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/quickbooks/connect`,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to start QuickBooks connect."));
    }
};

export const submitDisconnectQuickBooks = async (
    businessId: string,
): Promise<{ success: boolean }> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/quickbooks/disconnect`,
            {},
            withBusiness(businessId),
        );
        return res.data.data || { success: true };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to disconnect QuickBooks."));
    }
};

export const submitGetQuickBooksStatus = async (
    businessId: string,
): Promise<IQuickBooksConnection | null> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/quickbooks/status`,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load QuickBooks status."));
    }
};

// ── Sync ────────────────────────────────────────────────────────────────
export const submitSyncCustomers = async (
    businessId: string,
): Promise<ISyncJob> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/quickbooks/sync/customers`,
            {},
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to sync customers."));
    }
};

export const submitSyncInvoices = async (
    businessId: string,
): Promise<ISyncJob> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/quickbooks/sync/invoices`,
            {},
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to sync invoices."));
    }
};

export const submitSyncAll = async (businessId: string): Promise<ISyncJob> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/quickbooks/sync/all`,
            {},
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to sync from QuickBooks."));
    }
};

export const submitListSyncJobs = async (
    businessId: string,
): Promise<ISyncJob[]> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/sync/jobs`,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load sync history."));
    }
};

// ── Customers ───────────────────────────────────────────────────────────
export const submitListCustomers = async (
    businessId: string,
    query: IListCustomersQuery = {},
): Promise<ICollectPagedResponse<ICollectCustomer>> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/customers`,
            withBusiness(businessId, { params: query }),
        );
        return {
            items: Array.isArray(res.data?.data) ? res.data.data : [],
            nextCursor: res.data?.pageInfo?.nextCursor ?? null,
            total: res.data?.pageInfo?.total,
        };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load customers."));
    }
};

export const submitGetCustomer = async (
    businessId: string,
    customerId: string,
): Promise<ICollectCustomer> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/customers/${customerId}`,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load customer."));
    }
};

// ── Invoices ────────────────────────────────────────────────────────────
export const submitListInvoices = async (
    businessId: string,
    query: IListInvoicesQuery = {},
): Promise<ICollectPagedResponse<ICollectInvoice>> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/invoices`,
            withBusiness(businessId, { params: query }),
        );
        return {
            items: Array.isArray(res.data?.data) ? res.data.data : [],
            nextCursor: res.data?.pageInfo?.nextCursor ?? null,
            total: res.data?.pageInfo?.total,
        };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load invoices."));
    }
};

export const submitGetInvoice = async (
    businessId: string,
    invoiceId: string,
): Promise<ICollectInvoice> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/invoices/${invoiceId}`,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load invoice."));
    }
};

export const submitGetArAging = async (
    businessId: string,
): Promise<IArAgingSummary> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/invoices/aging/summary`,
            withBusiness(businessId),
        );
        // Backend shape: { buckets: { current, "1-30", "31-60", "61-90", "90+" }, totals: { count, balance } }
        // Frontend shape: { currency, totalOutstanding, invoiceCount, buckets: [{label, count, total}] }
        const raw = res.data?.data ?? {};
        const bucketsObj: Record<string, { count: number; totalBalance: number }> =
            raw.buckets ?? {};
        const keyMap: Record<string, string> = {
            current: "current",
            "1-30": "1_30",
            "31-60": "31_60",
            "61-90": "61_90",
            "90+": "90_plus",
        };
        const order = ["current", "1-30", "31-60", "61-90", "90+"];
        const buckets = order
            .filter((k) => bucketsObj[k])
            .map((k) => ({
                label: keyMap[k] || k,
                count: bucketsObj[k]?.count ?? 0,
                total: bucketsObj[k]?.totalBalance ?? 0,
            }));
        return {
            currency: raw.currency || "USD",
            totalOutstanding: raw.totals?.balance ?? 0,
            invoiceCount: raw.totals?.count ?? 0,
            buckets,
            generatedAt: raw.generatedAt,
        };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load AR aging summary."));
    }
};

export const submitGetDashboardOverview = async (
    businessId: string,
    limit = 8,
): Promise<IDashboardOverview> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/dashboard/overview`,
            { ...withBusiness(businessId), params: { limit } },
        );
        return res.data?.data as IDashboardOverview;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load dashboard overview."));
    }
};

export const submitGetCollectionsMetrics = async (
    businessId: string,
    range: CollectionsRange = "4w",
): Promise<ICollectionsMetrics> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/dashboard/collections`,
            { ...withBusiness(businessId), params: { range } },
        );
        return res.data?.data as ICollectionsMetrics;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load collections metrics."));
    }
};

/**
 * Drill-down behind a clickable dashboard KPI card. Returns the same universe
 * of records the card counts, enriched with recovery action + payer history.
 */
export const submitGetMetricBreakdown = async (
    businessId: string,
    metric: KpiBreakdownMetric,
    limit = 100,
    range: CollectionsRange = "4w",
): Promise<IKpiBreakdown> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/dashboard/breakdown`,
            { ...withBusiness(businessId), params: { metric, limit, range } },
        );
        return res.data?.data as IKpiBreakdown;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load card details."));
    }
};

/**
 * Autopilot reassurance summary: whether automation is on, how many invoices
 * are being worked vs. need a human, the last action taken, and the next 7
 * days. All derived from synced source-of-truth data.
 */
export const submitGetAutopilotSummary = async (
    businessId: string,
): Promise<IAutopilotSummary> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/dashboard/autopilot`,
            withBusiness(businessId),
        );
        return res.data?.data as IAutopilotSummary;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load autopilot status."));
    }
};

/**
 * Business-wide activity feed ("What we did for you"). Paginated by the
 * `before` ISO cursor returned as `nextBefore`.
 */
export const submitGetActivityFeed = async (
    businessId: string,
    limit = 20,
    before?: string,
): Promise<IActivityFeedPage> => {
    try {
        const params: Record<string, any> = { limit };
        if (before) params.before = before;
        const res = await axios.get(`${API_BASE_URL}/collect/activity`, {
            ...withBusiness(businessId),
            params,
        });
        return res.data?.data as IActivityFeedPage;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load activity feed."));
    }
};

/**
 * Action Center triage queue: the prioritised open action items the platform
 * paused for a human, each with invoice context and a recommended next action.
 */
export const submitGetActionCenter = async (
    businessId: string,
    limit = 8,
): Promise<IActionCenter> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/dashboard/action-center`,
            { ...withBusiness(businessId), params: { limit } },
        );
        return res.data?.data as IActionCenter;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load the action center."));
    }
};

/**
 * Recovery Impact scorecard: cash recovered, how fast invoices got paid, the
 * speed trend vs the previous period, and on-time recovery share.
 */
export const submitGetRecoveryImpact = async (
    businessId: string,
    range: ImpactRange = "3m",
): Promise<IRecoveryImpact> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/dashboard/recovery-impact`,
            { ...withBusiness(businessId), params: { range } },
        );
        return res.data?.data as IRecoveryImpact;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load recovery impact."));
    }
};

/**
 * Relationship Safety signals: customers shielded from chasing, tone
 * gentleness, opt-outs respected, concerns caught, reliable customers.
 */
export const submitGetRelationshipSafety = async (
    businessId: string,
): Promise<IRelationshipSafety> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/dashboard/relationship-safety`,
            withBusiness(businessId),
        );
        return res.data?.data as IRelationshipSafety;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load relationship safety."));
    }
};

export const submitGetRecoveryFunnel = async (
    businessId: string,
): Promise<IRecoveryFunnel> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/analytics/funnel`,
            withBusiness(businessId),
        );
        return res.data?.data as IRecoveryFunnel;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load recovery funnel."));
    }
};

export const submitGetRecoveryHealth = async (
    businessId: string,
): Promise<IRecoveryHealth> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/analytics/health`,
            withBusiness(businessId),
        );
        return res.data?.data as IRecoveryHealth;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load recovery health."));
    }
};

export const submitGetRecoveryTrends = async (
    businessId: string,
    days = 30,
): Promise<IRecoveryTrends> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/analytics/trends`,
            { ...withBusiness(businessId), params: { days } },
        );
        return res.data?.data as IRecoveryTrends;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load recovery trends."));
    }
};

// ── Dunning Campaigns ───────────────────────────────────────────────────
export const submitListCampaigns = async (
    businessId: string,
): Promise<IDunningCampaign[]> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/dunning/campaigns`,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load campaigns."));
    }
};

export const submitGetCampaign = async (
    businessId: string,
    campaignId: string,
): Promise<IDunningCampaign> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/dunning/campaigns/${campaignId}`,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load campaign."));
    }
};

export const submitCreateCampaign = async (
    businessId: string,
    payload: IUpsertCampaignPayload,
): Promise<IDunningCampaign> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/dunning/campaigns`,
            payload,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to create campaign."));
    }
};

export const submitUpdateCampaign = async (
    businessId: string,
    campaignId: string,
    payload: IUpsertCampaignPayload,
): Promise<IDunningCampaign> => {
    try {
        const res = await axios.put(
            `${API_BASE_URL}/collect/dunning/campaigns/${campaignId}`,
            payload,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to update campaign."));
    }
};

export const submitDeleteCampaign = async (
    businessId: string,
    campaignId: string,
): Promise<{ success: boolean }> => {
    try {
        const res = await axios.delete(
            `${API_BASE_URL}/collect/dunning/campaigns/${campaignId}`,
            withBusiness(businessId),
        );
        return res.data.data || { success: true };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to delete campaign."));
    }
};

// ── Dunning Templates ───────────────────────────────────────────────────
export const submitListTemplates = async (
    businessId: string,
    query: IListTemplatesQuery = {},
): Promise<IReminderTemplate[]> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/dunning/templates`,
            withBusiness(businessId, { params: query }),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load templates."));
    }
};

export const submitGetTemplate = async (
    businessId: string,
    templateId: string,
): Promise<IReminderTemplate> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/dunning/templates/${templateId}`,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load template."));
    }
};

export const submitCreateTemplate = async (
    businessId: string,
    payload: IUpsertTemplatePayload,
): Promise<IReminderTemplate> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/dunning/templates`,
            payload,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to create template."));
    }
};

export const submitUpdateTemplate = async (
    businessId: string,
    templateId: string,
    payload: IUpsertTemplatePayload,
): Promise<IReminderTemplate> => {
    try {
        const res = await axios.put(
            `${API_BASE_URL}/collect/dunning/templates/${templateId}`,
            payload,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to update template."));
    }
};

export const submitDeleteTemplate = async (
    businessId: string,
    templateId: string,
): Promise<{ success: boolean }> => {
    try {
        const res = await axios.delete(
            `${API_BASE_URL}/collect/dunning/templates/${templateId}`,
            withBusiness(businessId),
        );
        return res.data.data || { success: true };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to delete template."));
    }
};

// ── Dunning Activity + Manual actions ───────────────────────────────────
export const submitListDunningEvents = async (
    businessId: string,
    query: IListDunningEventsQuery = {},
): Promise<ICollectPagedResponse<IDunningEvent>> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/dunning/events`,
            withBusiness(businessId, { params: query }),
        );
        return {
            items: Array.isArray(res.data?.data) ? res.data.data : [],
            nextCursor: res.data?.pageInfo?.nextCursor ?? null,
            total: res.data?.pageInfo?.total,
        };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load dunning activity."));
    }
};

export const submitRunDunningNow = async (
    businessId: string,
): Promise<{ processed: number; sent: number; skipped: number; failed: number }> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/dunning/run`,
            {},
            withBusiness(businessId),
        );
        const raw = res.data?.data || {};
        // Backend returns { invoicesScanned, remindersSent, remindersSkipped,
        // remindersFailed, ... } — normalize to the shape the UI expects.
        return {
            processed: Number(raw.invoicesScanned ?? raw.processed ?? 0),
            sent: Number(raw.remindersSent ?? raw.sent ?? 0),
            skipped: Number(raw.remindersSkipped ?? raw.skipped ?? 0),
            failed: Number(raw.remindersFailed ?? raw.failed ?? 0),
        };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to run dunning now."));
    }
};

export const submitSendManualReminder = async (
    businessId: string,
    invoiceId: string,
    payload: ISendManualReminderPayload,
): Promise<IDunningEvent> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/invoices/${invoiceId}/send-reminder`,
            payload,
            withBusiness(businessId),
        );
        return res.data.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to send reminder."));
    }
};

export const submitGetInvoiceTimeline = async (
    businessId: string,
    invoiceId: string,
): Promise<IInvoiceTimelineEntry[]> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/invoices/${invoiceId}/timeline`,
            withBusiness(businessId),
        );
        // Backend shape: { invoice, timeline: [{ type, at, data }] }.
        // Frontend wants: [{ kind, occurredAt, data }].
        const raw = res.data?.data?.timeline ?? res.data?.data ?? [];
        const items = Array.isArray(raw) ? raw : [];
        return items.map((it: any) => ({
            kind: it.type === "webhook" ? "webhook" : "dunning",
            occurredAt: it.at || it.occurredAt,
            data: it.data,
        })) as IInvoiceTimelineEntry[];
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load invoice timeline."));
    }
};

// ── Recovery state machine (Stage 2A) ───────────────────────────────────
export const submitGetRecoveryStateMetadata = async (
    businessId: string,
): Promise<IRecoveryStateMetadata[]> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/recovery/states`,
            withBusiness(businessId),
        );
        return (res.data?.data ?? []) as IRecoveryStateMetadata[];
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load recovery state metadata."));
    }
};

export const submitTransitionInvoiceState = async (
    businessId: string,
    invoiceId: string,
    payload: ITransitionInvoiceStatePayload,
): Promise<{
    transitioned: boolean;
    from: string | null;
    to: string;
    invoiceId: string;
    skipReason?: string;
}> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/invoices/${invoiceId}/transition`,
            payload,
            withBusiness(businessId),
        );
        return res.data?.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to transition invoice state."));
    }
};

export const submitListInvoiceTransitions = async (
    businessId: string,
    invoiceId: string,
): Promise<IRecoveryStateTransition[]> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/invoices/${invoiceId}/transitions`,
            withBusiness(businessId),
        );
        return (res.data?.data ?? []) as IRecoveryStateTransition[];
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load invoice state history."));
    }
};

// ── Promise-to-Pay (Stage 2B) ───────────────────────────────────────────
export const submitCreatePromise = async (
    businessId: string,
    invoiceId: string,
    payload: ICreatePromisePayload,
): Promise<IPromiseToPay> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/invoices/${invoiceId}/promises`,
            payload,
            withBusiness(businessId),
        );
        return res.data?.data as IPromiseToPay;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to create promise."));
    }
};

export const submitListPromisesForInvoice = async (
    businessId: string,
    invoiceId: string,
): Promise<IPromiseToPay[]> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/invoices/${invoiceId}/promises`,
            withBusiness(businessId),
        );
        return (res.data?.data ?? []) as IPromiseToPay[];
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load promises."));
    }
};

export const submitListPromisesForBusiness = async (
    businessId: string,
    query: IListPromisesQuery = {},
): Promise<ICollectPagedResponse<IPromiseToPay>> => {
    try {
        const res = await axios.get(`${API_BASE_URL}/collect/promises`, {
            ...withBusiness(businessId),
            params: query,
        });
        const data = res.data?.data ?? {};
        return {
            items: data.items ?? [],
            nextCursor: data.nextCursor ?? null,
        };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load promises."));
    }
};

export const submitMarkPromiseKept = async (
    businessId: string,
    promiseId: string,
    payload: IResolvePromisePayload = {},
): Promise<IPromiseToPay> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/promises/${promiseId}/kept`,
            payload,
            withBusiness(businessId),
        );
        return res.data?.data as IPromiseToPay;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to mark promise kept."));
    }
};

export const submitMarkPromiseBroken = async (
    businessId: string,
    promiseId: string,
    payload: IResolvePromisePayload = {},
): Promise<IPromiseToPay> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/promises/${promiseId}/broken`,
            payload,
            withBusiness(businessId),
        );
        return res.data?.data as IPromiseToPay;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to mark promise broken."));
    }
};

export const submitCancelPromise = async (
    businessId: string,
    promiseId: string,
    payload: IResolvePromisePayload = {},
): Promise<IPromiseToPay> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/promises/${promiseId}/cancel`,
            payload,
            withBusiness(businessId),
        );
        return res.data?.data as IPromiseToPay;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to cancel promise."));
    }
};

// ── Recovery Queue + Escalation (Stage 2C) ─────────────────────
function serializeRecoveryQueueQuery(
    query: IRecoveryQueueQuery,
): Record<string, any> {
    const out: Record<string, any> = {};
    if (query.state) {
        out.state = Array.isArray(query.state)
            ? query.state.join(",")
            : query.state;
    }
    if (query.escalationLevel) {
        out.escalationLevel = Array.isArray(query.escalationLevel)
            ? query.escalationLevel.join(",")
            : query.escalationLevel;
    }
    if (query.assignedToUserId) out.assignedToUserId = query.assignedToUserId;
    if (typeof query.minBalance === "number")
        out.minBalance = query.minBalance;
    if (typeof query.minDaysOverdue === "number")
        out.minDaysOverdue = query.minDaysOverdue;
    if (query.cursor) out.cursor = query.cursor;
    if (typeof query.limit === "number") out.limit = query.limit;
    return out;
}

export const submitListRecoveryQueue = async (
    businessId: string,
    query: IRecoveryQueueQuery = {},
): Promise<IRecoveryQueuePage> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/recovery/queue`,
            withBusiness(businessId, {
                params: serializeRecoveryQueueQuery(query),
            }),
        );
        return res.data?.data as IRecoveryQueuePage;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load recovery queue."));
    }
};

export const submitGetRecoveryQueueSummary = async (
    businessId: string,
): Promise<IRecoveryQueueSummary> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/recovery/summary`,
            withBusiness(businessId),
        );
        return res.data?.data as IRecoveryQueueSummary;
    } catch (e: any) {
        throw new Error(
            pickError(e, "Failed to load recovery queue summary."),
        );
    }
};

export const submitAssignInvoice = async (
    businessId: string,
    invoiceId: string,
    payload: IAssignInvoicePayload,
): Promise<ICollectInvoice> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/invoices/${invoiceId}/assign`,
            payload,
            withBusiness(businessId),
        );
        return res.data?.data as ICollectInvoice;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to assign invoice."));
    }
};

export const submitManualEscalateInvoice = async (
    businessId: string,
    invoiceId: string,
): Promise<IEscalationDecision> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/invoices/${invoiceId}/escalate`,
            {},
            withBusiness(businessId),
        );
        return res.data?.data as IEscalationDecision;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to escalate invoice."));
    }
};

// ─────────────────────────────────────────────────────────────────────
// Stage 2D — Customer reply inbox
// ─────────────────────────────────────────────────────────────────────
export interface IRepliesPage {
    items: ICustomerReply[];
    nextCursor: string | null;
}

function serializeRepliesQuery(
    query: IListRepliesQuery,
): Record<string, any> {
    const out: Record<string, any> = {};
    if (query.status) {
        out.status = Array.isArray(query.status)
            ? query.status.join(",")
            : query.status;
    }
    if (query.intent) {
        out.intent = Array.isArray(query.intent)
            ? query.intent.join(",")
            : query.intent;
    }
    if (query.invoiceId) out.invoiceId = query.invoiceId;
    if (query.customerId) out.customerId = query.customerId;
    if (query.cursor) out.cursor = query.cursor;
    if (typeof query.limit === "number") out.limit = query.limit;
    return out;
}

export const submitListReplies = async (
    businessId: string,
    query: IListRepliesQuery = {},
): Promise<IRepliesPage> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/replies`,
            withBusiness(businessId, {
                params: serializeRepliesQuery(query),
            }),
        );
        return res.data?.data as IRepliesPage;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load replies."));
    }
};

export const submitGetRepliesSummary = async (
    businessId: string,
): Promise<IRepliesSummary> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/replies/summary`,
            withBusiness(businessId),
        );
        return res.data?.data as IRepliesSummary;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load replies summary."));
    }
};

export const submitGetReply = async (
    businessId: string,
    replyId: string,
): Promise<ICustomerReply> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/replies/${replyId}`,
            withBusiness(businessId),
        );
        return res.data?.data as ICustomerReply;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load reply."));
    }
};

export const submitReprocessReply = async (
    businessId: string,
    replyId: string,
): Promise<ICustomerReply> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/replies/${replyId}/reprocess`,
            {},
            withBusiness(businessId),
        );
        return res.data?.data as ICustomerReply;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to reprocess reply."));
    }
};

export const submitResolveReply = async (
    businessId: string,
    replyId: string,
    payload: IResolveReplyPayload = {},
): Promise<ICustomerReply> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/replies/${replyId}/resolve`,
            payload,
            withBusiness(businessId),
        );
        return res.data?.data as ICustomerReply;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to resolve reply."));
    }
};

export const submitManualIngestReply = async (
    businessId: string,
    payload: IManualIngestReplyPayload,
): Promise<ICustomerReply> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/replies/ingest`,
            payload,
            withBusiness(businessId),
        );
        return res.data?.data as ICustomerReply;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to ingest reply."));
    }
};

// ─────────────────────────────────────────────────────────────────────────
// Stage 2F — SMS provisioning (Twilio)
// ─────────────────────────────────────────────────────────────────────────

export interface ITwilioSmsStatus {
    configured: boolean;
    smsEnabled: boolean;
    platformMessagingServiceSid?: string | null;
    twilio: {
        phoneNumber: string | null;
        friendlyName: string | null;
        messagingServiceSid: string | null;
        provisionedAt: string | null;
        releasedAt: string | null;
    } | null;
    webhooks: { inbound: string | null; status: string | null };
}

export interface ITwilioAvailableNumber {
    phoneNumber: string;
    friendlyName: string;
    locality?: string;
    region?: string;
    isoCountry?: string;
    capabilities?: {
        SMS?: boolean;
        MMS?: boolean;
        voice?: boolean;
    };
}

export const submitGetSmsStatus = async (
    businessId: string,
): Promise<ITwilioSmsStatus> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/sms/status`,
            withBusiness(businessId),
        );
        return res.data?.data as ITwilioSmsStatus;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load SMS status."));
    }
};

export const submitSearchSmsNumbers = async (
    businessId: string,
    params: { country?: string; areaCode?: string; contains?: string; limit?: number } = {},
): Promise<ITwilioAvailableNumber[]> => {
    try {
        const query = new URLSearchParams();
        if (params.country) query.set("country", params.country);
        if (params.areaCode) query.set("areaCode", params.areaCode);
        if (params.contains) query.set("contains", params.contains);
        if (params.limit) query.set("limit", String(params.limit));
        const res = await axios.get(
            `${API_BASE_URL}/collect/sms/available?${query.toString()}`,
            withBusiness(businessId),
        );
        return (res.data?.data?.numbers || []) as ITwilioAvailableNumber[];
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to search numbers."));
    }
};

export const submitProvisionSms = async (
    businessId: string,
    payload: { phoneNumber: string; friendlyName?: string },
): Promise<{ phoneSid: string; phoneNumber: string; smsEnabled: boolean }> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/sms/provision`,
            payload,
            withBusiness(businessId),
        );
        return res.data?.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to provision number."));
    }
};

export const submitReleaseSms = async (
    businessId: string,
): Promise<void> => {
    try {
        await axios.delete(
            `${API_BASE_URL}/collect/sms/provision`,
            withBusiness(businessId),
        );
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to release number."));
    }
};

export const submitUpdateMessagingService = async (
    businessId: string,
    messagingServiceSid: string,
): Promise<{ messagingServiceSid: string | null }> => {
    try {
        const res = await axios.put(
            `${API_BASE_URL}/collect/sms/messaging-service`,
            { messagingServiceSid },
            withBusiness(businessId),
        );
        return res.data?.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to update Messaging Service."));
    }
};

export const submitTestSms = async (
    businessId: string,
    payload: { to: string; body: string },
): Promise<{ sid: string; status: string }> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/sms/test`,
            payload,
            withBusiness(businessId),
        );
        return res.data?.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to send test SMS."));
    }
};

// ── Help & Support: end-user tickets ────────────────────────────────────
export const submitCreateSupportTicket = async (
    businessId: string,
    payload: ICreateSupportTicketPayload,
): Promise<ISupportTicket> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/support/tickets`,
            payload,
            withBusiness(businessId),
        );
        return res.data?.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to submit support ticket."));
    }
};

export const submitListMySupportTickets = async (
    businessId: string,
    params: { status?: string; page?: number; limit?: number } = {},
): Promise<ISupportTicketListPage> => {
    try {
        const res = await axios.get(`${API_BASE_URL}/collect/support/tickets`, {
            ...withBusiness(businessId),
            params,
        });
        const { items, page, limit, total, totalPages, hasNext, hasPrev } =
            res.data;
        return { items, page, limit, total, totalPages, hasNext, hasPrev };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load your support tickets."));
    }
};

export const submitGetMySupportTicket = async (
    businessId: string,
    ticketId: string,
): Promise<ISupportTicketDetail> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/support/tickets/${ticketId}`,
            withBusiness(businessId),
        );
        return res.data?.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load ticket."));
    }
};

export const submitAddSupportTicketMessage = async (
    businessId: string,
    ticketId: string,
    body: string,
): Promise<ISupportTicketDetail> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/support/tickets/${ticketId}/messages`,
            { body },
            withBusiness(businessId),
        );
        return res.data?.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to send your reply."));
    }
};

export const submitReopenSupportTicket = async (
    businessId: string,
    ticketId: string,
): Promise<ISupportTicket> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/support/tickets/${ticketId}/reopen`,
            {},
            withBusiness(businessId),
        );
        return res.data?.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to reopen ticket."));
    }
};

// ── Help & Support: super-admin Incident Queue (not business-scoped) ────
export const submitListAdminSupportTickets = async (
    query: IAdminSupportListQuery = {},
): Promise<IAdminSupportTicketListPage> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/admin/support/tickets`,
            { params: query },
        );
        const { items, page, limit, total, totalPages, hasNext, hasPrev } =
            res.data;
        return { items, page, limit, total, totalPages, hasNext, hasPrev };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load the incident queue."));
    }
};

export const submitGetAdminSupportTicket = async (
    ticketId: string,
): Promise<IAdminSupportTicketDetail> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/admin/support/tickets/${ticketId}`,
        );
        return res.data?.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load ticket."));
    }
};

export const submitUpdateAdminSupportTicket = async (
    ticketId: string,
    payload: IAdminUpdateSupportTicketPayload,
): Promise<ISupportTicket> => {
    try {
        const res = await axios.patch(
            `${API_BASE_URL}/collect/admin/support/tickets/${ticketId}`,
            payload,
        );
        return res.data?.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to update ticket."));
    }
};

export const submitAddAdminSupportMessage = async (
    ticketId: string,
    body: string,
    isInternalNote: boolean,
): Promise<ISupportTicketMessage> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/admin/support/tickets/${ticketId}/messages`,
            { body, isInternalNote },
        );
        return res.data?.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to add message."));
    }
};

export const submitResolveAdminSupportTicket = async (
    ticketId: string,
    note?: string,
): Promise<ISupportTicket> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/admin/support/tickets/${ticketId}/resolve`,
            { note },
        );
        return res.data?.data;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to resolve ticket."));
    }
};
